import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, UserCheck, AlertTriangle, ClipboardList } from 'lucide-react';
import { useITQueue } from '../hooks/useTickets';
import { useAuth } from '../hooks/useAuth';
import Badge from '../components/Badge';
import SLAIndicator from '../components/SLAIndicator';
import Button from '../components/Button';
import { Card, DataTable } from '../components/ui';
import type { Column } from '../components/ui';
import type { Ticket } from '../types';

function StatCard({ icon: Icon, label, value, tone }: { icon: typeof Inbox; label: string; value: number; tone: string }) {
  return (
    <Card className="flex-1">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon size={20} /></div>
        <div>
          <div className="text-2xl font-bold text-navy leading-none">{value}</div>
          <div className="text-xs text-gray-500 mt-1">{label}</div>
        </div>
      </div>
    </Card>
  );
}

export default function AgentWorkspace() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tickets, loading } = useITQueue();

  const { mine, unassigned, breaching } = useMemo(() => {
    const mine = tickets.filter(t => t.assignedToId === user?.id || t.assignedTo?.id === user?.id);
    const unassigned = tickets.filter(t => !t.assignedToId && !t.assignedTo?.id);
    const breaching = tickets.filter(t => t.slaDueAt && new Date(t.slaDueAt).getTime() - Date.now() < 4 * 3600_000);
    return { mine, unassigned, breaching };
  }, [tickets, user?.id]);

  const baseCols: Column<Ticket>[] = [
    { key: 'id', header: 'Reference', sortValue: t => t.id, render: t => <span className="font-mono text-xs text-brand-dark">{t.id}</span> },
    { key: 'requester', header: 'Requester', sortValue: t => t.requester?.name ?? '', render: t => t.requester?.name ?? '—' },
    { key: 'category', header: 'Category', render: t => t.items?.[0]?.category || '—' },
    { key: 'status', header: 'Status', sortValue: t => t.status, render: t => <Badge status={t.status} small /> },
    { key: 'sla', header: 'SLA', render: t => <SLAIndicator ticket={t} compact /> },
  ];

  // "Assign" opens the full ticket detail page — the actual assign panel (choose self or
  // a specific IT staff member, with a reason if not self) lives there, not as an instant
  // one-click self-assign shortcut from this table.
  const unassignedCols: Column<Ticket>[] = [
    ...baseCols,
    { key: 'action', header: '', align: 'right', render: t => (
      <Button size="sm" variant="secondary" onClick={() => navigate(`/tickets/${t.id}`, { state: { activeNav: '/workspace' } })}><ClipboardList size={13} /> Assign</Button>
    ) },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <h1 className="text-xl font-bold text-navy">Agent Workspace</h1>

      <div className="flex flex-col sm:flex-row gap-3">
        <StatCard icon={UserCheck}    label="Assigned to me" value={mine.length}       tone="bg-brand/10 text-brand" />
        <StatCard icon={Inbox}        label="Unassigned"     value={unassigned.length} tone="bg-amber-100 text-amber-600" />
        <StatCard icon={AlertTriangle} label="SLA < 4h"       value={breaching.length}  tone="bg-red-100 text-red-600" />
      </div>

      <div>
        <h2 className="text-md font-semibold text-navy mb-2">My Queue</h2>
        <Card flush>
          <DataTable columns={baseCols} data={mine} rowKey={t => t.id} loading={loading}
            onRowClick={t => navigate(`/tickets/${t.id}`, { state: { activeNav: '/workspace' } })} pageSize={10}
            emptyTitle="Nothing assigned to you" emptyDescription="Pick up a ticket from the unassigned queue below." />
        </Card>
      </div>

      <div>
        <h2 className="text-md font-semibold text-navy mb-2">Unassigned Queue</h2>
        <Card flush>
          <DataTable columns={unassignedCols} data={unassigned} rowKey={t => t.id} loading={loading}
            onRowClick={t => navigate(`/tickets/${t.id}`, { state: { activeNav: '/workspace' } })} pageSize={10}
            emptyTitle="Queue is clear" emptyDescription="No unassigned tickets right now." />
        </Card>
      </div>
    </div>
  );
}
